// src/components/Procurement/OrderPaymentPanel.jsx
// ORDER-CONTEXT payment (Item 3a, owner redesign). An order can owe MORE THAN ONE supplier — goods to
// its goods supplier, each landed cost to its own LC supplier — so a single "pay the supplier" form
// can never settle the whole order. This panel instead lists the order's OPEN DUES (one bucket per
// supplier/kind) sourced from the cash-application ledger (aged-payables reads v_payment_allocation_net,
// so the numbers are correct independently of the engine's LC placeholder), lets the user pay each
// bucket a partial amount from ONE cash/bank source, and PINS every payment to THIS order's obligation.
//
// Why the pin: a real Supplier Payment auto-allocates PURE FIFO oldest-first (allocateSettlementLeg only
// self-attributes credits/cancellations, not payments), so a payment for this order's goods would land
// on the supplier's OLDEST open order instead. After posting each bucket leg we reconcile it via
// POST /payment-allocations to this order's matching obligation (operation_id + kind) — which releases
// the auto-FIFO apply and re-applies here. One cash outflow, N legs (one per supplier account), each
// pinned. General-context (header / Data Entry) payments keep supplier-scoped FIFO + Payments-page
// reallocation — this panel is ONLY for a specific order.
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import Combobox from '../Combobox';
import { isPickableFunding } from '../../lib/resolveSupplierAccount';   // 565: shared account-picker rule
import useActionLabel from '../../i18n/useActionLabel';
import { Emo, emoText } from '../../shell/EmojiIcons';
import { TextInput } from '../inputs/Input';
import MoneyTextInput from '../MoneyTextInput';

const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
const r2  = (n) => Math.round((Number(n) || 0) * 100) / 100;
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function OrderPaymentPanel({ operationId, onClose, onSuccess, title }) {
  const al = useActionLabel();
  const [agedRows, setAgedRows] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [acctName, setAcctName] = useState({});   // id -> account name (for the confirmation from→to)
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [date, setDate]     = useState(todayStr());
  const [notes, setNotes]   = useState('');
  const [amounts, setAmounts] = useState({});   // bucket.key -> string
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState(null);
  const [results, setResults] = useState(null);  // [{ label, amount, pinned }]

  useEffect(() => {
    api.request('/reports/aged-payables').then(r => setAgedRows(Array.isArray(r) ? r : [])).catch(() => setAgedRows([]));
    api.getAccounts().then(d => {
      const list = d || [];
      setAccountOptions(list.filter(a => isPickableFunding(a, ['cash', 'bank'])).map(a => ({ value: a.id, label: a.name })));
      setAcctName(Object.fromEntries(list.map(a => [a.id, a.name])));
    }).catch(() => {});
  }, []);

  // One bucket per (account, kind) — goods vs landed cost — from THIS order's open obligations.
  const buckets = useMemo(() => {
    if (!agedRows) return [];
    const m = new Map();
    for (const row of agedRows) {
      for (const it of (row.items || [])) {
        if (it.operation_id !== operationId || !(it.amount > 0.005)) continue;
        const kind = it.subtype === 'landed_cost' ? 'lc' : 'goods';
        const key = `${it.account_id}|${kind}`;
        // 770: carry the aged item's `type` too (alongside `subtype`, same first-item-wins rule) —
        // it is the ONLY handle that tells an EXPENSE obligation apart from a goods invoice. Display
        // handle only: not in the bucket key, so grouping/amounts/aging are byte-identical.
        if (!m.has(key)) m.set(key, { key, kind, type: it.type, subtype: it.subtype, accountId: it.account_id, partnerId: it.partner_id, supplierName: it.partner_name || row.partner_name || 'Supplier', due: 0, dueDate: it.due_date || null });
        const b = m.get(key);
        b.due = r2(b.due + it.amount);
        if (it.due_date && (!b.dueDate || it.due_date < b.dueDate)) b.dueDate = it.due_date;
      }
    }
    return [...m.values()].sort((a, b) => (a.kind === 'goods' ? 0 : 1) - (b.kind === 'goods' ? 0 : 1));
  }, [agedRows, operationId]);

  // Label a due by its real nature — not every non-LC obligation is "Goods": an order-less obligation
  // (a transfer/advance or an opening balance, subtype Other/Opening) must not be mislabeled "Goods".
  // 770: nor an ORDER-LESS EXPENSE. A credit expense booked straight onto the payable (765/766) is
  // type='Expense' with a free-text expense subtype ('Fuel', 'Livestock', …) — never 'landed_cost' —
  // so it fell through every branch to the "Goods" default while having no purchase line at all.
  // Discriminator = the aged item's own `type`, not 769's `isOrderLessItem`: that predicate is a
  // SUPERSET (it also matches the transfer/advance and Payment legs this function already names
  // distinctly), so reusing it here would collapse three correct labels into one. `type` is exact.
  // Checked BEFORE the subtype branches because 765's expense was posted as Expense/Other, which
  // `s === 'other'` would otherwise call a transfer. LC stays first: an LC leg is type='Purchase'.
  // 772: nor an order-less PAYMENT leg — a manual Payable→Bank movement. It is type='Payment', and its
  // subtype is free: 'Other' on some legs, EMPTY on others (OP-20260725-142928, 90 000), so the subtype
  // test caught only half of them and the bare ones fell through to "Goods". Owner ruling: they are all
  // one thing — UNIFIED on `type`. Safe to put ahead of the
  // subtype tests: 'Opening balance' is NOT a Payment (an opening posts type='Opening'/subtype='Opening',
  // api server.js:8488-8489), so nothing that used to read 'Opening balance' can be captured here.
  // 782 FIX 1: the string is 'Transfer / advance', not 772's bare 'Transfer' — the owner's ruling for
  // OP-20260725-142928 names that exact label, and it is the string the `s === 'other'` branch two lines
  // below has always emitted. Keeping two spellings for one thing (Payment/'' -> 'Transfer',
  // Payment/'Other' -> 'Transfer / advance' before 772; both -> 'Transfer' after 772) was the split 772
  // set out to remove; this makes the unified label the one the owner named. Display-only: no amount,
  // bucket, due date or sort key reads dueLabel().
  const dueLabel = (b) => {
    if (b.kind === 'lc') return 'Landed cost';
    if (String(b.type || '') === 'Expense') return 'Expense';
    if (String(b.type || '') === 'Payment') return 'Transfer / advance';
    const s = String(b.subtype || '').toLowerCase();
    if (s === 'other') return 'Transfer / advance';
    if (s === 'opening') return 'Opening balance';
    return 'Goods';
  };

  // Default each bucket's input to its full due (partial editable).
  useEffect(() => {
    const seed = {};
    for (const b of buckets) seed[b.key] = String(b.due);
    setAmounts(seed);
  }, [buckets]);

  const setAmt = (key, v, cap) => {
    let s = v.replace(/[^0-9.]/g, '');
    if (cap != null && parseFloat(s) > cap) s = String(cap);
    setAmounts(a => ({ ...a, [key]: s }));
  };

  const totalToPay = buckets.reduce((s, b) => s + (parseFloat(amounts[b.key]) || 0), 0);
  const anyAmount = totalToPay > 0.005;
  // App-wide rule: a future-dated cash movement can't be 'posted' — it is saved as 'planned' (it posts
  // on its date). A planned payment is not real cash yet, so it does NOT settle dues (no allocation pin).
  const isFuture = date > todayStr();
  // Distinct supplier AP accounts being paid → each is its own finance leg (one payment per supplier).
  const payingSupplierCount = new Set(buckets.filter(b => (parseFloat(amounts[b.key]) || 0) > 0.005).map(b => b.accountId)).size;

  async function pay() {
    if (!sourceAccountId) { setError('Select the cash/bank account to pay from.'); return; }
    if (!anyAmount)       { setError('Enter an amount for at least one due.'); return; }
    setSubmitting(true); setError(null);
    const done = [];
    try {
      for (const b of buckets) {
        const amt = r2(parseFloat(amounts[b.key]) || 0);
        if (amt <= 0.005) continue;
        // 1) post the cash outflow → this supplier's AP account (partner-scoped for shared "Various").
        const leg = await api.request('/finance', {
          method: 'POST',
          body: JSON.stringify({
            type: 'Payment', subtype: 'Supplier Payment', amount: amt,
            account_from_id: sourceAccountId, account_to_id: b.accountId,
            partner_id: b.partnerId || null, operation_id: operationId,
            date, notes: notes || null, tx_status: isFuture ? 'planned' : 'posted',
          }),
        });
        // 2) pin it to THIS order's obligation of this kind (overrides the auto-FIFO oldest-first).
        // A PLANNED (future) payment isn't real cash yet — it doesn't settle anything, so there's
        // nothing to pin; it FIFO-applies when it posts on its date (re-pin later if needed).
        let pinned = false;
        if (leg?.id && !isFuture) {
          try {
            const oblig = await api.request(`/payment-allocations?settlement_leg_id=${leg.id}`);
            const t = (oblig.open_obligations || []).find(o => o.operation_id === operationId && o.kind === b.kind);
            if (t) {
              const put = Math.min(amt, r2(t.open + t.allocated_here));
              await api.request('/payment-allocations', {
                method: 'POST',
                body: JSON.stringify({ settlement_leg_id: leg.id, allocations: [{ invoice_leg_id: t.invoice_leg_id, amount: put }] }),
              });
              pinned = true;
            }
          } catch { /* leg is posted; pin is best-effort (falls back to auto-FIFO) */ }
        }
        done.push({
          label: `${dueLabel(b)} · ${b.supplierName}`,
          opId: leg?.operation_id || null, amount: amt,
          from: acctName[sourceAccountId] || 'Account',
          to: acctName[b.accountId] || b.supplierName, pinned, planned: isFuture,
        });
      }
      setResults(done);
      onSuccess && onSuccess(done);
    } catch (e) {
      setError(e.message || 'Payment failed.');
    } finally { setSubmitting(false); }
  }

  const card = { background: '#f4f8f5', borderBottom: '1px solid var(--line)', padding: '14px 20px' };
  const lbl  = { fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 };
  const inp  = { border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', fontSize: 12, width: '100%', boxSizing: 'border-box' };

  if (results) {
    const grand = results.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const planned = results.some(r => r.planned);
    return (
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 2 }}><Emo name="check" /> {planned ? 'Payment scheduled' : 'Payment recorded'} — {fmt(grand)} F</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
          {planned
            ? `Saved as planned — it posts on ${date} and settles this order's dues then (applied oldest-first; re-pin on the Payments page if needed).`
            : results.length > 1 ? `${results.length} payments posted (one per supplier), each attributed to ${operationId}.` : `Attributed to ${operationId}.`}
        </div>
        <div style={{ border: '1px solid var(--line)', borderRadius: 6, background: '#fff', overflow: 'hidden', maxWidth: 640 }}>
          {results.map((r, i) => (
            <div key={i} style={{ padding: '8px 12px', borderTop: i ? '1px solid var(--line2)' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>{emoText(r.label)}</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.amount)} F</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span>Ref <strong style={{ color: 'var(--ink)' }}>{r.opId || '—'}</strong></span>
                <span>·</span>
                <span>{r.from} → {r.to}</span>
                {r.planned ? <span style={{ color: '#c47d10' }}>· planned</span> : !r.pinned && <span style={{ color: '#c47d10' }}>· auto-applied (not pinned)</span>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn success sm" onClick={() => onClose && onClose()} >Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{title || al('makeSupplierPayment')}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
        This order's open dues — pay any amount of each from one account. Each payment is attributed to this order.
      </div>

      {agedRows == null ? (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading dues…</div>
      ) : buckets.length === 0 ? (
        <div style={{ fontSize: 12.5, color: '#166534', background: '#dcfce7', borderRadius: 6, padding: '8px 12px' }}>
          Nothing open on this order — it is fully paid.
        </div>
      ) : (
        <>
          {/* shared source + date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12, maxWidth: 640 }}>
            <div>
              <label style={lbl}>Pay from</label>
              <Combobox options={accountOptions} value={sourceAccountId} onChange={setSourceAccountId} placeholder="Cash / bank account" />
            </div>
            <div>
              <label style={lbl}>Payment date</label>
              <TextInput type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
            </div>
          </div>

          {/* dues */}
          <div style={{ border: '1px solid var(--line)', borderRadius: 6, background: '#fff', overflow: 'hidden', maxWidth: 640 }}>
            {buckets.map((b, i) => {
              const cap = b.due;
              return (
                <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderTop: i ? '1px solid var(--line2)' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>
                      {dueLabel(b)}
                      <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: b.kind === 'lc' ? '#c47d10' : '#1b4332', background: b.kind === 'lc' ? '#fdf3e2' : '#e7f2ec', borderRadius: 9999, padding: '2px 9px' }}>{b.supplierName}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{fmt(b.due)} F due{b.dueDate ? ` · due ${b.dueDate}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MoneyTextInput value={amounts[b.key] ?? ''} onChange={e => setAmt(b.key, e.target.value, cap)} inputMode="decimal"
                      style={{ width: 110, textAlign: 'right', ...inp }} />
                    <button type="button" onClick={() => setAmt(b.key, String(cap), cap)} title="Pay full due"
                      style={{ fontSize: 10, border: '1px solid var(--line)', background: '#fff', borderRadius: 4, padding: '3px 6px', cursor: 'pointer' }}>full</button>
                    <button type="button" onClick={() => setAmt(b.key, '0', cap)} title="Skip"
                      style={{ fontSize: 10, border: '1px solid var(--line)', background: '#fff', borderRadius: 4, padding: '3px 6px', cursor: 'pointer' }}>skip</button>
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 12px', borderTop: '2px solid var(--line)', background: '#f9fafb' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Total to pay</span>
              <span style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}>{fmt(totalToPay)} F</span>
            </div>
          </div>

          <div style={{ marginTop: 10, maxWidth: 640 }}>
            <label style={lbl}>Notes (optional)</label>
            <TextInput type="text" value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder="Optional notes" />
          </div>

          {isFuture && anyAmount && (
            <div style={{ fontSize: 11.5, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '7px 10px', marginTop: 10, maxWidth: 640 }}>
              This date is in the future — it will be saved as <strong>planned</strong> and settle this order's dues when it posts on {date} (applied oldest-first then; re-pin on the Payments page if needed).
            </div>
          )}
          {payingSupplierCount > 1 && (
            <div style={{ fontSize: 11.5, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '7px 10px', marginTop: 10, maxWidth: 640 }}>
              This covers {payingSupplierCount} suppliers, so it posts as <strong>{payingSupplierCount} separate payments</strong> in the ledger (one per supplier) — each attributed to this order.
            </div>
          )}

          {error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn success sm" onClick={pay} disabled={submitting || !anyAmount}
              style={{ opacity: (submitting || !anyAmount) ? 0.6 : 1 }}>
              {submitting ? 'Processing…' : `${isFuture ? 'Schedule' : 'Pay'} ${fmt(totalToPay)} F`}
            </button>
            <button onClick={() => onClose && onClose()} style={{ fontSize: 12, background: 'none', border: '1px solid var(--ctl-border)', borderRadius: 5, padding: '6px 12px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
